import React, { Suspense, useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { matchPath, useLocation, Route } from 'react-router-dom';
import ReactPlaceholder from 'react-placeholder/lib';
import { CSSTransition } from 'react-transition-group';

import PopupListener from '../components/PopupListener';
import { HeaderTopPlaceholder } from '../components/placeholders/HeaderPlaceholders';
import { FooterTopPlaceholder } from '../components/placeholders/FooterPlaceholders';

import { BackgroundTypeEnum, BackgroundPositionEnum } from '../components/base/BackgroundsEnum';

import { Constants } from '../constants/Constants';
import { RoutePath } from '../constants/SitePaths';
import { template } from '../constants/Configs';


import Utils from '../services/Utils';
import { LanguageService } from '../services/LanguageService';
import { Browser } from '../services/helpers/Browser';

import AppAdviser from '../components/base/appAdvisor/AppAdviser';
import InfoBlock from '../components/header/InfoBlock';
import { LocalStorageService } from '../services/LocalStorageService';
import PopupEnum from '../components/base/PopupEnum';
import StylesHelper from '../services/helpers/StylesHelper';
import { getPage } from '../actions/PagesActions';
import { useDeviceType } from '../hooks';
import { HeaderHeightProvider } from './context/HeaderHeightContext';
import UrlHelper from '../services/helpers/UrlHelper';
const SeoContent = React.lazy(() => import('../components/base/SeoContent'));
const SportBetVerificationPopup = React.lazy(() => import('../components/base/popups/SportBetVerificationPopup'));
const SideBarMenu = React.lazy(() => import('../components/header/sidebar/SideBarMenu'));
const SiteHeader = React.lazy(() => import('../components/header'));
const SiteFooter = React.lazy(() => import('../components/footer'));
const FloatingMenu = React.lazy(() => import('../components/base/floatingMenu/FloatingMenu'));
const Announcement = React.lazy(() => import('../components/base/announcement/Announcement'));
const Page = React.lazy(() => import('../components/pages/Page'))

/* Main Layout */
export const MainLayout = ({ children, matchProps, ...rest }) => {
  // Get data from Redux store
  const dispatch = useDispatch()
  const { showAnnouncements, seoRulesEnabled, homePageInfo } = useSelector((state) => state.settings);
  const [showAppAdviser, setShowAppAdviser] = useState(null);
  const popupVisible = useSelector((state) => state.ui.popupVisible);

  const { sideBar, menuContent, mobileNavBar } = useSelector((state) => state.ui.controls);
  const { page, isLoading: isPageLoading } = useSelector(state => state.pages);
  // Ref and state variables
  const wrapperRef = useRef(null);
  const [plc, setPlc] = useState(true);
  const [isAnnouncementVisible, setAnnuncementVisibility] = useState(showAnnouncements);
  const [isAppAdviserVisible, setAppAdviserVisibility] = useState(showAppAdviser);
  const mainBlockRef = useRef(null);
  const { search, pathname } = useLocation();
  const params = matchPath(pathname, { path: `/${LanguageService.getSelectedLanguage()}${RoutePath.WIDGET_EDITOR}` })
  const isNotWidgetPreview = !(Utils.isPreviewMode() && params)
  const { hideHeader } = Utils.parseQuery(search);
  const hidePWAppElements = (hideHeader === 'true' && Browser.isMobileApp()) || rest.isFullLayout || Browser.isMobNativeApp();
  const [isCustomHomePage, setCustomHomePage] = useState(false);
  const [childData, setChildData] = useState('');

  // Destructure necessary properties
  const mainMenu = menuContent?.nestedControls?.find((item) => item.name === 'MainMenu');
  const secondaryMenu = menuContent?.nestedControls?.find((item) => item.name === 'SecondaryMenu');
  const sidebarMainMenu = sideBar?.nestedControls?.find((item) => item.name === 'MainMenu');
  const sidebarSecondaryMenu = sideBar?.nestedControls?.find((item) => item.name === 'SecondaryMenu');
  const showSidebarIcons = (sidebarMainMenu && mainMenu && sidebarMainMenu?.properties.showItemIcon === 'true') ||
    (sidebarSecondaryMenu && secondaryMenu && sidebarSecondaryMenu?.properties.showItemIcon === 'true');

  const bodyBg = rest.pageBgData && rest.pageBgData
    .filter((data) => data.backgroundType === BackgroundTypeEnum.Body)
    .filter((element) => {
      const elementPages = element.pages.filter((el) => el.prefix + el.alias === rest.alias || el.prefix + el.alias + '-lobby' === rest.alias);
      return element.forAllPages ? true : elementPages && elementPages.length ? true : false;
    });

  const footerBg = rest.pageBgData &&
    rest.pageBgData.filter((data) => data.backgroundType === BackgroundTypeEnum.Footer)
      .filter((element) => {
        let elementPages;
        if (rest.alias === '/games/') {
          elementPages = element.pages.filter((el) => el.prefix + el.alias === rest.alias + matchProps.match.params.gameId);
        } else if (rest.alias === '/pages/') {
          elementPages = element.pages.filter((el) => (el.prefix + el.alias === rest.alias + matchProps.match.params.pageId) || (el.id === +matchProps.match.params.pageId));
        } else {
          elementPages = element.pages.filter((el) => el.prefix + el.alias === rest.alias);
        }
        return element.forAllPages === true ? true : elementPages && elementPages.length ? true : false;
      });

  // Function to get main block style
  const getMainStyle = () => {
    const background = bodyBg && bodyBg[0];
    // const position = bodyBgPosition && bodyBgPosition[0];
    const topBannerHeight = document.querySelector('.top-banner-section')?.clientHeight;
    if (background) {
      const bgImg = background.imageUrl ? background.imageUrl: UrlHelper.getStaticFileUrl(`/img/backgrounds/${background.id}${background.extension}?v=${background.lastModifiedDate}`);
      return {
        minHeight: topBannerHeight,
        backgroundImage: `url("${bgImg}")`,
      };
    }
    return { minHeight: topBannerHeight };
  };

  // Set Header Datas
  const receiveChildData = (data) => {
    setChildData(data);
  };

  // Handle Announcements visibility
  useEffect(() => {
    setAnnuncementVisibility(showAnnouncements);
  }, [showAnnouncements]);


  // Handle App Adviser visibility
  useEffect(() => {
    if (!LocalStorageService.get('app_advisor_terminated')) {
      setAppAdviserVisibility(showAppAdviser);
    } else {
      setAppAdviserVisibility(false);
    }
  }, [showAppAdviser]);

  // Match the  page path
  const matchPage = (path) => matchPath(matchProps.history.location.pathname, {
    path: `/:lang?${path}`,
    exact: true,
    strict: false,
  });
  const {isMobile} = useDeviceType();
  const isHomeRoute = !!(matchPage(RoutePath.DEFAULT) || matchPage(RoutePath.HOMEPAGE));
  const showSportBetVerification = popupVisible === PopupEnum.SportBetVerification && (isHomeRoute || !!(matchPage(RoutePath.SPORT) || matchPage(RoutePath.ESPORT)));
  const showSeoContent = seoRulesEnabled && isNotWidgetPreview && rest.alias !== Constants.GAME_INFO && !Browser.isMobileApp();
  const isShowFloatingMenu = isHomeRoute && !isMobile;

  useEffect(() => {
    if (isHomeRoute && homePageInfo?.isSystem === false) {
      setCustomHomePage(true)
      dispatch(getPage({ pageId: homePageInfo.id, pageContent: 1 }))
    } else {
      setCustomHomePage(false)
    }
  }, [homePageInfo, isHomeRoute,isMobile])

  return (
    <>
      {showSportBetVerification && <Suspense fallback={<></>}><SportBetVerificationPopup /></Suspense>}
      {rest.isFullLayout ?
        <div
          className={`wrapper wrapper-full-screen ${bodyBg && bodyBg.length ? StylesHelper.getBgSize(bodyBg) : ''} 
          ${bodyBg && bodyBg.length && bodyBg[0].backgroundPosition === BackgroundPositionEnum.Fixed ? 'wrapper-img_fixed' : ''}`}
          style={getMainStyle()}>
          {isCustomHomePage ? <Suspense fallback={<></>}><Page page={page} isPageLoading={isPageLoading} isPreview={false} /></Suspense> : <>{children}</>}
          <div>
            {seoRulesEnabled && rest.alias !== Constants.GAME_INFO && <Suspense fallback={<></>}><SeoContent alias={rest.alias} /></Suspense>}
          </div>
        </div> :
        <div ref={wrapperRef}
          className={`wrapper 
            ${childData}
            ${template && template === 7 ? 'tb--temp-folk' : ''}
            ${hidePWAppElements ? 'tb--hide-pw-app-elements' : ''} 
        ${isAnnouncementVisible ? 'tb--has-announcement' : ''} 
        ${isAppAdviserVisible ? 'tb--has-app-adviser' : ''}
            ${plc ? 'has-plc' : ''} ${isHomeRoute ? 'tb--has-floating-menu' : ''} ${mobileNavBar ? 'tb--has-mobile-nav floating--ico-up-navbar' : ''} 
        ${sideBar?.nestedControls?.length && ((mainMenu?.nestedControls?.length && sidebarMainMenu) || (secondaryMenu?.nestedControls?.length && sidebarSecondaryMenu)) ? 'tb--has-sidebar' : ''}
        ${sideBar?.nestedControls?.length && ((mainMenu?.nestedControls?.length && sidebarMainMenu) || (secondaryMenu?.nestedControls?.length && sidebarSecondaryMenu)) && showSidebarIcons ? 'show-sidebar_block' : ''}`
          }>
          {isMobile && !Browser.isMobileApp() ? (
            <AppAdviser onHide={setAppAdviserVisibility} setShowAppAdviser={setShowAppAdviser} />
          ) : (
            <></>
          )}
          {isAnnouncementVisible && !hidePWAppElements ? (
            <Suspense fallback={<></>}><Announcement onHide={() => setAnnuncementVisibility(false)} /></Suspense>
          ) : (
            <></>
          )}
          <Suspense fallback={<></>}>
            {!isMobile && <SideBarMenu />}
          </Suspense>
          {hidePWAppElements ? (
            <></>
          ) : (
            <ReactPlaceholder
              customPlaceholder={<HeaderTopPlaceholder />}
              ready={!rest.isSettingsLoading}
            >
              <Suspense fallback={<></>}>
                <SiteHeader
                  onSendData={receiveChildData}
                  hasSideBar={sideBar?.nestedControls?.length &&
                    ((mainMenu?.nestedControls?.length && sidebarMainMenu) ||
                      (secondaryMenu?.nestedControls?.length && sidebarSecondaryMenu))
                    ? true : false}
                />
              </Suspense>
            </ReactPlaceholder>
          )}
          {isShowFloatingMenu ? (
            <Suspense fallback={<></>}><FloatingMenu isSettingsLoading={rest.isSettingsLoading} /></Suspense>
          ) : null}
          <CSSTransition in={true} appear={true} timeout={600} classNames="fade">
            <main
              ref={mainBlockRef}
              className={`main-block ${bodyBg && bodyBg.length ? StylesHelper.getBgSize(bodyBg) : ''} ${popupVisible === PopupEnum.SportBetVerification ? 'tb--static' : ''} 
               ${window.location.pathname == '/' + LanguageService.getSelectedLanguage() + '/sport' ||
                  window.location.pathname === '/' + LanguageService.getSelectedLanguage() + '/esport' ? '' : 'main-h_100'} 
                  ${bodyBg && bodyBg.length && bodyBg[0].backgroundPosition === BackgroundPositionEnum.Fixed ? 'main-img_fixed' : ''} `}
              style={getMainStyle()}>
              {hidePWAppElements ? (
                <></>
              ) : (
                <InfoBlock></InfoBlock>
              )}
              <div className="l5--sidebar tb--flex tb--flex-col">
                <div
                  {...(!sideBar?.nestedControls?.length
                    ? { style: { width: '100%' } }
                    : {})}
                  className="l5--main"
                >
                  {isCustomHomePage ? <Suspense fallback={<></>}><Page page={page} isPageLoading={isPageLoading} isPreview={false} /></Suspense> : <>{children}</>}
                </div>
              </div>
              {showSeoContent && <div className='main-block__seo-content-wrapper'><Suspense fallback={<></>}><SeoContent isSettingsLoading={rest.isSettingsLoading} alias={rest.alias} /> </Suspense></div>}
            </main>
          </CSSTransition>
         
        </div >}
          {hidePWAppElements ? (
            <></>
          ) : (
            <ReactPlaceholder
              customPlaceholder={<FooterTopPlaceholder />}
              ready={!rest.isSettingsLoading}
            >
              <Suspense fallback={<></>}>
                <SiteFooter
                  bgSize={StylesHelper.getBgSize}
                  bgImagaData={footerBg}
                  hasNavBar={sideBar?.nestedControls?.length && ((mainMenu?.nestedControls?.length && sidebarMainMenu) || (secondaryMenu?.nestedControls?.length && sidebarSecondaryMenu)) && showSidebarIcons}
                />
              </Suspense>
            </ReactPlaceholder>
          )}
    </>
  );
};

/*
  Private route wrapper
 */

export const MainLayoutRoute = ({
  component: Component,
  isSettingsLoading,
  language,
  placeholder: PlaceHolder,
  alias,
  pageBgData,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(matchProps) => {
       const params = matchProps.match.params;
       const { name, providerId, page } = params;
        //RULES ANG GAME INFO login register  colision temp fix 
      if (!!rest.alternativeComponent && [page, providerId, name].includes('popup')) {
          Component = rest.alternativeComponent;
          matchProps.match.path = rest.alternativePath || matchProps.match.path;
          matchProps.match.params.menuId = page;
         matchProps.match.params.subMenuId = providerId;
        }
 
        return isSettingsLoading || !language ? (
          PlaceHolder ? (
            <PlaceHolder />
          ) : (
            <></>
          )
        ) : (
          <HeaderHeightProvider>
            <MainLayout
              key={language}
              isSettingsLoading={isSettingsLoading}
              alias={alias}
              pageBgData={pageBgData}
              matchProps={PopupListener.getParams(matchProps)}
            >
              <Suspense fallback={PlaceHolder ? <PlaceHolder /> : <></>}>
                {!isSettingsLoading ? (
                  <Component
                    alias={alias}
                    {...PopupListener.getParams(matchProps)}
                  />
                ) : null}
              </Suspense>
            </MainLayout>
          </HeaderHeightProvider>
        );
      }}
    />
  );
};
